import React, { useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import { useSelector } from "react-redux";
import {
  formatDate,
  formatDateTime,
  orderItemLineLabel
} from "../Orders/utils/orderUtils";
import { useFormatCurrency } from "../../hooks/useFormatCurrency";
import { PaymentMethodLabels } from "../Payment/enums/paymentMethod";
import { Receipt } from "./types/receipt";
import { Invoice } from "../Invoice/types/invoice";
import type { Order } from "../Orders/types/salesOrder";
import { BRAND_COLOR, BRAND_HOVER } from "@kivunova/kivufrontendcommon";
import { RootState } from "../../store/store";
import ReceiptPrintLayout from "../PrintDocuments/ReceiptPrintLayout";
import {
  buildReceiptPrintLayoutProps,
  resolvePrintStoreId
} from "../PrintDocuments/printDocumentMappers";
import { usePrintOrderLineDisplayNames } from "../../hooks/usePrintOrderLineDisplayNames";

export interface ReceiptDetailPrintableBodyProps {
  receipt: Receipt;
  invoiceDetail: Invoice | null | undefined;
  /**
   * Invoice fetched for this receipt (e.g. `getInvoiceById` result). Preferred over
   * Redux `invoiceDetail` so paid/remaining stay correct when global state is stale or
   * print runs before the store updates (partial payments).
   */
  invoiceLinked?: Invoice | null;
  /** Sales order lines + totals for print; fetch with useGetOrder(receipt.orderId). */
  orderForPrint: Order | null;
  invoiceDetailPath: string;
  paymentDetailPath: string;
  salesOrderDetailPath: string;
  /** List modal: close before following a link to another detail route */
  onBeforeEntityNavigate?: () => void;
  t: (key: string, fallback: string) => string;
}

const entityLinkSx = {
  color: BRAND_COLOR,
  textDecoration: "underline",
  textUnderlineOffset: 2,
  textAlign: "right" as const,
  "&:hover": { color: BRAND_HOVER }
};

/** Print cashier line: prefer first name, then last name, then email local-part. */
function cashierDisplayForPrint(
  user:
    | {
        firstName?: string | null;
        lastName?: string | null;
        email?: string | null;
      }
    | undefined,
  unknownFallback: string
): string {
  if (!user) return unknownFallback;
  const first = user.firstName?.trim() ?? "";
  if (first) return first;
  const last = user.lastName?.trim() ?? "";
  if (last) return last;
  const email = user.email?.trim() ?? "";
  if (email) {
    const at = email.indexOf("@");
    const local = at > 0 ? email.slice(0, at).trim() : email;
    if (local) return local;
  }
  return unknownFallback;
}

/**
 * Shared receipt detail markup used by the full page and the list modal (print + screen).
 */
const ReceiptDetailPrintableBody: React.FC<ReceiptDetailPrintableBodyProps> = ({
  receipt,
  invoiceDetail,
  invoiceLinked,
  orderForPrint,
  invoiceDetailPath,
  paymentDetailPath,
  salesOrderDetailPath,
  onBeforeEntityNavigate,
  t
}) => {
  const formatCurrency = useFormatCurrency();
  const companyName = useSelector(
    (state: RootState) => state.company.companyName
  );
  const storeList = useSelector((state: RootState) => state.store.storeList);
  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const { displayNameByItemId } = usePrintOrderLineDisplayNames(orderForPrint);

  const invoiceForReceipt = useMemo(() => {
    if (invoiceLinked?.invoiceId === receipt.invoiceId) return invoiceLinked;
    if (invoiceDetail?.invoiceId === receipt.invoiceId) return invoiceDetail;
    return undefined;
  }, [invoiceDetail, invoiceLinked, receipt.invoiceId]);

  const printProps = useMemo(
    () =>
      buildReceiptPrintLayoutProps(
        receipt,
        invoiceForReceipt,
        orderForPrint,
        companyName,
        storeList.find(
          (s) =>
            s.id ===
            resolvePrintStoreId(orderForPrint, invoiceForReceipt ?? null)
        ),
        cashierDisplayForPrint(
          userDetails,
          t("receipt-print-cashier-unknown", "—")
        ),
        displayNameByItemId,
        t
      ),
    [
      receipt,
      invoiceForReceipt,
      orderForPrint,
      companyName,
      storeList,
      userDetails,
      displayNameByItemId,
      t
    ]
  );

  // Only render line items when the order has been fetched and has items
  const lineItems =
    orderForPrint && Array.isArray(orderForPrint.items)
      ? orderForPrint.items
      : [];

  return (
    <>
      {/* ── Print-only layout ───────────────────────────────────────────── */}
      <Box sx={{ display: "none", "@media print": { display: "block" } }}>
        <ReceiptPrintLayout {...printProps} />
      </Box>

      {/* ── Screen-only layout ──────────────────────────────────────────── */}
      <Box sx={{ "@media print": { display: "none !important" } }}>
        {/* ── Receipt header card ──────────────────────────────────────── */}
        <Paper
          className="receipt-content"
          sx={{
            p: 3,
            mb: 3,
            "@media print": {
              boxShadow: "none",
              border: "none",
              backgroundColor: "transparent"
            }
          }}
        >
          <Typography variant="h4" fontWeight="bold" mb={2}>
            {t("receipt-detail-title", "Receipt Detail")}
          </Typography>

          <Typography variant="body2" color="text.secondary" mb={2}>
            {t("receipt-detail-invoice-number-label", "Invoice Number")}:{" "}
            <Typography
              component="span"
              fontWeight="medium"
              color="text.primary"
            >
              {receipt.invoiceNumber}
            </Typography>
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Stack spacing={2}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="body2" color="text.secondary">
                {t("receipt-detail-invoice", "Invoice")}:
              </Typography>
              <Typography
                component={RouterLink}
                to={invoiceDetailPath}
                data-testid="receipt-invoice-link"
                variant="body2"
                fontWeight="medium"
                sx={entityLinkSx}
                onClick={() => onBeforeEntityNavigate?.()}
              >
                {t("view-invoice", "View Invoice")}
              </Typography>
            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="body2" color="text.secondary">
                {t("receipt-detail-payment", "Payment")}:
              </Typography>
              <Typography
                component={RouterLink}
                to={paymentDetailPath}
                variant="body2"
                fontWeight="medium"
                sx={entityLinkSx}
                onClick={() => onBeforeEntityNavigate?.()}
              >
                {t("view-payment", "View Payment")}
              </Typography>
            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="body2" color="text.secondary">
                {t("receipt-detail-order-number", "Order Number")}:
              </Typography>
              <Typography
                component={RouterLink}
                to={salesOrderDetailPath}
                variant="body2"
                fontWeight="medium"
                sx={entityLinkSx}
                onClick={() => onBeforeEntityNavigate?.()}
              >
                {receipt.orderNumber}
              </Typography>
            </Box>

            <Divider />

            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {t("receipt-detail-payment-method", "Payment Method")}:
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {PaymentMethodLabels[receipt.paymentMethod] ||
                  receipt.paymentMethod}
              </Typography>
            </Box>

            {receipt.reference ? (
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {t("receipt-detail-reference", "Reference")}:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {receipt.reference}
                </Typography>
              </Box>
            ) : null}

            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {t("receipt-detail-payment-date", "Payment Date")}:
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {formatDate(receipt.paymentDate)}
              </Typography>
            </Box>

            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {t("receipt-detail-created", "Created")}:
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {formatDateTime(receipt.createdAt)}
              </Typography>
            </Box>

            <Divider />

            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" fontWeight="bold">
                {t("receipt-detail-amount", "Amount")}:
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {formatCurrency(receipt.amount)}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* ── Items Purchased card ─────────────────────────────────────── */}
        {lineItems.length > 0 && (
          <Paper
            className="receipt-content"
            sx={{
              p: 3,
              mb: 3,
              "@media print": {
                boxShadow: "none",
                border: "none",
                backgroundColor: "transparent"
              }
            }}
          >
            <Typography variant="h6" fontWeight="bold" mb={2}>
              {t("receipt-detail-items-purchased", "Items Purchased")}
            </Typography>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t("receipt-detail-item-name", "Item")}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {t("receipt-detail-item-qty", "Qty")}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {t("receipt-detail-item-unit-price", "Unit Price")}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {t("receipt-detail-item-line-total", "Total")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lineItems.map((item) => {
                    // Resolve the best display name: persisted variation/product
                    // name from the order, then fall back to the inventory-fetched
                    // name (displayNameByItemId), then itemId.
                    const fetchedName =
                      displayNameByItemId[item.itemId] ?? null;
                    const label = orderItemLineLabel(item, fetchedName);
                    return (
                      <TableRow
                        key={item.itemId}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 }
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {label}
                          </Typography>
                          {item.detail?.unit && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {item.detail.unit}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {item.quantity}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {formatCurrency(item.unitPrice)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium">
                            {formatCurrency(item.lineTotal)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* ── Invoice Summary card ─────────────────────────────────────── */}
        {invoiceForReceipt ? (
          <Paper
            className="receipt-content"
            sx={{
              p: 3,
              mb: 3,
              "@media print": {
                boxShadow: "none",
                border: "none",
                backgroundColor: "transparent"
              }
            }}
          >
            <Typography variant="h6" fontWeight="bold" mb={2}>
              {t("receipt-detail-summary", "Invoice Summary")}
            </Typography>
            <Stack spacing={1.5}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {t("receipt-detail-subtotal", "Subtotal")}:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatCurrency(invoiceForReceipt.subtotalAmount)}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {t("discount", "Discount")}:
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="medium"
                  color="error.main"
                >
                  {formatCurrency(invoiceForReceipt.discountAmount ?? 0)}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {t("receipt-detail-tax", "Tax")}:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatCurrency(invoiceForReceipt.taxAmount)}
                </Typography>
              </Box>
              <Divider />
              {/* Fix: removed duplicate "Total Amount" row — the receipt header card
                  already shows the amount paid; Paid + Remaining below are sufficient
                  to convey the full payment picture without repeating totalAmount. */}
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {t("receipt-detail-paid-amount", "Paid")}:
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="medium"
                  color="success.main"
                >
                  {formatCurrency(invoiceForReceipt.paidAmount)}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {t("receipt-detail-remaining", "Remaining")}:
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color={
                    invoiceForReceipt.remainingAmount > 0.005
                      ? "error.main"
                      : "success.main"
                  }
                >
                  {formatCurrency(invoiceForReceipt.remainingAmount)}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        ) : null}
      </Box>
    </>
  );
};

export default ReceiptDetailPrintableBody;
